import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetConfigurationQuery,
  useUpdateConfigurationMutation,
  type NotificationConfig,
} from '@/redux/internal-services/onboardingApi';
import { StepShell } from './StepShell';
import type { StepHandle, SubmitResult } from './stepHandle';
import { Switch } from '@/components/UI/Switch';

interface Props {
  onBack?: () => void;
  onNext: () => void;
  onSkip?: () => void;
  disabled?: boolean;
}

const CHANNEL_LABELS: Record<string, string> = {
  in_app: 'In-App',
  push: 'Push',
  email: 'Email',
  sms: 'SMS',
};

const CLASSIFICATION_LABELS: Record<string, string> = {
  critical: 'Critical',
  important: 'Important',
  info: 'Info',
};

const Toggle: React.FC<{ checked: boolean; onChange: () => void; label: string }> = ({ checked, onChange, label }) => (
  <Switch checked={checked} onCheckedChange={onChange} aria-label={label} />
);

/**
 * Guided-setup "Notification Settings" step — per-classification × channel delivery matrix.
 * Reads/writes the generic `notification` configuration (tenant layer) so the same matrix is
 * editable later in Settings → Notifications.
 */
const NotificationSettingsStep = forwardRef<StepHandle, Props>(({ onBack, onNext, onSkip, disabled = false }, ref) => {
  const { t } = useTranslation();
  const { data: configResp } = useGetConfigurationQuery('notification');
  const [updateConfiguration, { isLoading: saving }] = useUpdateConfigurationMutation();

  const cfg = configResp?.data?.effective_value as NotificationConfig | undefined;
  const classifications = cfg?.classifications ?? ['critical', 'important', 'info'];
  const channels = cfg?.channels ?? ['in_app', 'push', 'email', 'sms'];

  const [matrix, setMatrix] = useState<Record<string, Record<string, boolean>>>({});
  const [error, setError] = useState('');

  // Synchronous mirror so submit/getState read the freshest matrix.
  const matrixRef = useRef(matrix);
  useEffect(() => { matrixRef.current = matrix; }, [matrix]);

  useEffect(() => {
    if (cfg?.matrix) setMatrix(cfg.matrix);
  }, [cfg]);

  const toggle = (cls: string, ch: string) =>
    setMatrix((prev) => ({
      ...prev,
      [cls]: { ...prev[cls], [ch]: !prev[cls]?.[ch] },
    }));

  // Voice actuator — set one cell to an explicit value (case-insensitive id match).
  const setMatrixCell = (cls: string, ch: string, enabled: boolean) => {
    const c = classifications.find((x) => x.toLowerCase() === cls.toLowerCase()) ?? cls;
    const h = channels.find((x) => x.toLowerCase() === ch.toLowerCase()) ?? ch;
    const next = { ...matrixRef.current, [c]: { ...matrixRef.current[c], [h]: enabled } };
    matrixRef.current = next;
    setMatrix(next);
  };

  const persist = async (): Promise<SubmitResult> => {
    setError('');
    try {
      await updateConfiguration({ key: 'notification', value: { matrix: matrixRef.current } }).unwrap();
      return { ok: true };
    } catch {
      setError(t('common.failedToSave'));
      return { ok: false, reason: 'transient', message: 'Failed to save notification settings.' };
    }
  };

  const handleNext = () => { void persist().then((r) => { if (r.ok) onNext(); }); };

  useImperativeHandle(ref, (): StepHandle => ({
    getState: () => ({ matrix: matrixRef.current, classifications, channels }),
    submit: persist,
    setMatrixCell,
  }));

  return (
    <StepShell
      title="Notification Settings"
      subtitle="Personalise your notification settings"
      onBack={onBack}
      onNext={handleNext}
      onSkip={onSkip}
      disabled={disabled}
      saving={saving}
      error={error}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-ui-text3 text-xs">
              <th className="pb-3 font-medium">Classification</th>
              {channels.map((ch) => (
                <th key={ch} className="pb-3 font-medium text-center">{CHANNEL_LABELS[ch] ?? ch}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {classifications.map((cls) => (
              <tr key={cls} className="border-t border-ui-border2">
                <td className="py-3 font-medium text-ui-text">{CLASSIFICATION_LABELS[cls] ?? cls}</td>
                {channels.map((ch) => (
                  <td key={ch} className="py-3 text-center">
                    <div className="flex justify-center">
                      <Toggle
                        checked={!!matrix[cls]?.[ch]}
                        onChange={() => toggle(cls, ch)}
                        label={`${CLASSIFICATION_LABELS[cls] ?? cls} notifications by ${CHANNEL_LABELS[ch] ?? ch}`}
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </StepShell>
  );
});

NotificationSettingsStep.displayName = 'NotificationSettingsStep';

export default NotificationSettingsStep;
